import api, {
  buildCompanyScopeHeaders,
} from "./api";

export const DEFAULT_COMMERCIAL_RULE_LIMIT = 50;

function normalizeNonNegativeInteger(
  value,
  fallback,
) {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isInteger(parsed) || parsed < 0) {
    return fallback;
  }

  return parsed;
}

function normalizePositiveInteger(
  value,
  fallback,
) {
  const parsed = Number.parseInt(value, 10);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
}

function normalizeOptionalString(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}

function requireNonEmptyString(
  value,
  fieldName,
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    throw new TypeError(
      `${fieldName} must be a non-empty string.`,
    );
  }

  return value.trim();
}

function buildCommercialRulesBasePath(
  contractUuid,
) {
  const normalizedContractUuid = requireNonEmptyString(
    contractUuid,
    "contractUuid",
  );

  return `/supply-chain/contracts/${encodeURIComponent(
    normalizedContractUuid,
  )}/commercial-rules`;
}

function buildCommercialRulePath(
  contractUuid,
  ruleUuid,
) {
  const normalizedRuleUuid = requireNonEmptyString(
    ruleUuid,
    "ruleUuid",
  );

  return `${buildCommercialRulesBasePath(
    contractUuid,
  )}/${encodeURIComponent(normalizedRuleUuid)}`;
}

export function buildCommercialRuleListParams({
  offset = 0,
  limit = DEFAULT_COMMERCIAL_RULE_LIMIT,
  ruleType = "",
} = {}) {
  const params = {
    offset: normalizeNonNegativeInteger(
      offset,
      0,
    ),
    limit: normalizePositiveInteger(
      limit,
      DEFAULT_COMMERCIAL_RULE_LIMIT,
    ),
  };

  const normalizedRuleType = normalizeOptionalString(
    ruleType,
  );

  if (normalizedRuleType) {
    params.rule_type = normalizedRuleType;
  }

  return params;
}

export function getCommercialRuleErrorMessage(
  error,
  fallback = "Unable to process the commercial rule request.",
) {
  const responseData = error?.response?.data;

  if (
    typeof responseData?.message === "string"
    && responseData.message.trim()
  ) {
    return responseData.message.trim();
  }

  if (
    typeof responseData?.error === "string"
    && responseData.error.trim()
  ) {
    return responseData.error.trim();
  }

  if (
    typeof error?.message === "string"
    && error.message.trim()
  ) {
    return error.message.trim();
  }

  return fallback;
}

export async function listCommercialRules(
  contractUuid,
  {
    companyUuid,
    offset = 0,
    limit = DEFAULT_COMMERCIAL_RULE_LIMIT,
    ruleType = "",
  } = {},
) {
  const response = await api.get(
    buildCommercialRulesBasePath(contractUuid),
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
      params: buildCommercialRuleListParams({
        offset,
        limit,
        ruleType,
      }),
    },
  );

  return response.data;
}

export async function getCommercialRule(
  contractUuid,
  ruleUuid,
  {
    companyUuid,
  } = {},
) {
  const response = await api.get(
    buildCommercialRulePath(
      contractUuid,
      ruleUuid,
    ),
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
    },
  );

  return response.data;
}

export async function createCommercialRule(
  contractUuid,
  payload,
  {
    companyUuid,
  } = {},
) {
  const response = await api.post(
    buildCommercialRulesBasePath(contractUuid),
    payload,
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
    },
  );

  return response.data;
}

export async function updateCommercialRule(
  contractUuid,
  ruleUuid,
  payload,
  {
    companyUuid,
  } = {},
) {
  const response = await api.patch(
    buildCommercialRulePath(
      contractUuid,
      ruleUuid,
    ),
    payload,
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
    },
  );

  return response.data;
}

export async function deleteCommercialRule(
  contractUuid,
  ruleUuid,
  {
    companyUuid,
  } = {},
) {
  await api.delete(
    buildCommercialRulePath(
      contractUuid,
      ruleUuid,
    ),
    {
      headers: buildCompanyScopeHeaders(
        companyUuid,
      ),
    },
  );
}
